import Card from '../ui/Card';
import Flex from '../ui/Flex';
import { Tabs, TabsList, TabsTrigger } from '../ui/Tabs';
import { useState } from 'react';
import {
  CartesianGrid,
  XAxis,
  YAxis,
  BarChart,
  Bar,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
} from 'recharts';
import useTrafficUsage, { TimeUnit } from '@/hooks/queries/useTrafficUsage';
import DateRangePicker from './DateRangePicker';
import { DateRange } from 'react-day-picker';
import {
  NameType,
  Payload,
} from 'recharts/types/component/DefaultTooltipContent';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/Select';
import { proxies } from '@/model/proxyTypes';
import { ProxyTypeName } from '@fd/common/biz/proxyType';
import { cn } from '@/lib/utils';
import CardTitle from '../ui/CardTitle';
import { DesktopView, MobileView } from '../responsive';
import useMobile from '@/hooks/useMobile';
import BrowserTranslateErrorCatcher from '@fd/frontend/components/BrowserTranslateErrorCatcher';

type ProxyUsageChartProps = {
  poolId?: number;
  className?: string;
  chartHeight?: number;
  chartWidth?: number;
  hideProxyTypeFilter?: boolean;
};

export default function ProxyUsageChart({
  poolId,
  className,
  chartHeight,
  chartWidth,
  hideProxyTypeFilter = false,
}: ProxyUsageChartProps) {
  const [timeUnit, setTimeUnit] = useState<TimeUnit>('day');
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    to: new Date(),
  });
  const [proxyType, setProxyType] = useState<ProxyTypeName | 'all'>('all');

  const { data } = useTrafficUsage({ timeUnit, dateRange, proxyType, poolId });

  return (
    <>
      <MobileView>
        <Card className="flex flex-col gap-3">
          <CardTitle>Traffic usage</CardTitle>
          {/* {!hideProxyTypeFilter && (
            <Select
              value={proxyType}
              onValueChange={(value) => setProxyType(value as ProxyTypeName)}
            >
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Select proxy type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All proxy types</SelectItem>
                {proxies.map((p) => (
                  <SelectItem key={p.type} value={p.type}>
                    {p.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )} */}
          <DateRangePicker onClosed={setDateRange} />
          <TimeUnitSelector timeUnit={timeUnit} setTimeUnit={setTimeUnit} />
        </Card>
      </MobileView>
      <Card className={cn('w-full max-w-[865px] px-3 sm:px-5', className)}>
        <Flex.Col className="gap-2 h-full">
          <DesktopView>
            <Flex.Row className="justify-between items-center">
              <Flex.Row className="gap-3 items-center">
                <div className="text-lg font-semibold">Traffic usage</div>
              </Flex.Row>
              <Flex.Row className="gap-3 items-center">
                {!hideProxyTypeFilter && (
                  <BrowserTranslateErrorCatcher>
                    <div translate="no">
                      <Select
                        value={proxyType}
                        onValueChange={(value) =>
                          setProxyType(value as ProxyTypeName)
                        }
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue placeholder="Select proxy type" />
                        </SelectTrigger>
                        <BrowserTranslateErrorCatcher>
                          <SelectContent>
                            <SelectItem value="all" translate="no">
                              All proxy types
                            </SelectItem>
                            {proxies.map((p) => (
                              <SelectItem
                                key={p.type}
                                value={p.type}
                                translate="no"
                              >
                                {p.title}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </BrowserTranslateErrorCatcher>
                      </Select>
                    </div>
                  </BrowserTranslateErrorCatcher>
                )}
                <DateRangePicker onClosed={setDateRange} />
                <TimeUnitSelector
                  timeUnit={timeUnit}
                  setTimeUnit={setTimeUnit}
                />
              </Flex.Row>
            </Flex.Row>
          </DesktopView>
          <Flex.Col className="h-full items-center justify-center w-full">
            <TrafficUsageChart
              data={data}
              height={chartHeight}
              width={chartWidth}
            />
          </Flex.Col>
        </Flex.Col>
      </Card>
    </>
  );
}

type TrafficUsageChartProps = {
  data: { label: string; gb_used: number }[];
  height?: number;
  width?: number;
};

function TrafficUsageChart({ data, height = 250 }: TrafficUsageChartProps) {
  const isMobile = useMobile();
  const axisFontSize = isMobile ? 10 : 12;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data}>
        <CartesianGrid strokeDasharray="3 3" />
        <ChartTooltip
          formatter={(value: number) => [`${value.toFixed(2)} GB`]}
          cursor={{ fill: 'transparent' }}
          content={({ payload, label }) => (
            <CustomTooltip payload={payload} label={label as string} />
          )}
        />
        <Bar dataKey="gb_used" className="fill-primary-purple/60  " />
        <XAxis
          dataKey="label"
          fontSize={axisFontSize}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          dataKey="gb_used"
          fontSize={axisFontSize}
          width={isMobile ? 35 : undefined}
          unit="GB"
          axisLine={false}
          tickLine={false}
          tickFormatter={(value) =>
            value >= 1 || value === 0 ? value.toFixed(0) : value.toFixed(2)
          }
          domain={[0, (dataMax: number) => Math.max(dataMax, 1)]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

function CustomTooltip({
  payload,
  label,
}: {
  payload?: Payload<number, NameType>[];
  label: string;
}) {
  return (
    <Card className="flex flex-col gap-2 text-sm p-2">
      <BrowserTranslateErrorCatcher>
        <p>Date: {label}</p>
        <p>Usage: {`${payload?.[0]?.value?.toFixed(2)} GB`}</p>
      </BrowserTranslateErrorCatcher>
    </Card>
  );
}

function TimeUnitSelector({
  timeUnit,
  setTimeUnit,
}: {
  timeUnit: TimeUnit;
  setTimeUnit: (timeUnit: TimeUnit) => void;
}) {
  return (
    <Tabs
      value={timeUnit}
      onValueChange={(value) => setTimeUnit(value as TimeUnit)}
    >
      <TabsList className="grid grid-cols-3 w-full sm:w-auto">
        <TabsTrigger value="hour">Hour</TabsTrigger>
        <TabsTrigger value="day">Day</TabsTrigger>
        <TabsTrigger value="month">Month</TabsTrigger>
      </TabsList>
    </Tabs>
  );
}
